import { useEffect, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { Router } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { selectUserRole } from 'src/old/actions/session/sessionSelectors';
import { selectIsLoading } from 'src/old/actions/loading/loadingSelectors';
import { LinearProgress } from '@mui/material';
import Modal from 'src/shared/components/Modals';
import Toasts from 'src/shared/components/Toast/index';
import { darkTheme, lightTheme } from './theme';
import routes from './routes';
import GoogleAnalytics from './shared/components/GoogleAnalytics';
import ScrollReset from './shared/components/ScrollReset';
import StylesProvider from './shared/components/StylesProvider';

import './mixins/main.scss';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/pt';
import 'moment/locale/it';
import 'moment/locale/fr';
import 'moment/locale/es';
import { userRoles } from './old/reducers/sessionReducer';

const AUTH_HARD_RESET_KEY = 'auth_hard_reset_v1_done';

const handleDarkMode = (pathName, set) => {
  if (['/employee/restricted'].some(darkModePath => darkModePath === pathName)) {
    set(true);
  } else {
    set(false);
  }
};

const App = ({ history }) => {
  const [initialPage, setInitialPage] = useState('/auth/login');
  const [routesList, setRoutesList] = useState(routes(initialPage));
  const role = useSelector(selectUserRole);
  const isLoading = useSelector(selectIsLoading);
  const [direction] = useState('ltr');
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const runOneTimeAuthHardReset = async () => {
      if (localStorage.getItem(AUTH_HARD_RESET_KEY) === '1') {
        return;
      }

      const onAuthPage = window.location.pathname.startsWith('/auth');
      const hasStoredState = Boolean(localStorage.getItem('state'));
      if (onAuthPage && !hasStoredState) {
        localStorage.setItem(AUTH_HARD_RESET_KEY, '1');
        return;
      }

      try {
        await fetch(`${process.env.REACT_APP_ZEUS_API_URL}/dashboard/logout`, {
          method: 'GET',
          credentials: 'include'
        });
      } catch (error) {
        // Best effort: continue with local cleanup even if backend logout fails.
      }

      localStorage.removeItem('state');
      sessionStorage.clear();
      localStorage.setItem(AUTH_HARD_RESET_KEY, '1');
      window.location.replace('/auth/login');
    };

    runOneTimeAuthHardReset();
  }, []);

  useEffect(() => {
    if (role) {
      setInitialPage(role === userRoles.ADMIN ? '/super/holdings' : '/overview');
    } else {
      setInitialPage('/auth/login');
    }
    setRoutesList(routes(initialPage));
    // eslint-disable-next-line
  }, [role]);

  useEffect(() => {
    handleDarkMode(history.location.pathname, setDarkMode);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    moment.locale([window.navigator.language, 'es']);
  }, []);

  history.listen(location => {
    handleDarkMode(location.pathname, setDarkMode);
  });
  return (
    <StyledEngineProvider injectFirst>
      <LocalizationProvider
        dateFormats={{ keyboardDate: 'DD/MM/YYYY' }}
        adapterLocale={navigator.language}
        dateAdapter={AdapterMoment}
      >
        <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
          <CssBaseline />
          {isLoading && <LinearProgress sx={{ position: 'fixed', top: 0, right: 0, width: '100%' }} />}
          <StylesProvider direction={direction}>
            <Router history={history}>
              <ScrollReset />
              <GoogleAnalytics />
              {renderRoutes(routesList)}
              <Modal />
            </Router>
            <Toasts />
          </StylesProvider>
        </ThemeProvider>
      </LocalizationProvider>
    </StyledEngineProvider>
  );
};

export default App;
